/**
 * Year6MemoryBookPage — /year6/:slug/memory-book
 *
 * The £99 money screen. Blurred preview + features list + Stripe checkout.
 * Uses the unauthenticated /api/events/{id}/year6/memory-book/checkout endpoint.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LockKey, Check, ArrowRight, Sparkle, BookOpen, Image as ImgIcon, ChatCircleText, VideoCamera, GraduationCap, ArrowClockwise } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Year6MemoryBookPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [eventId, setEventId] = useState(null);
  const [status, setStatus] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const unlocked = searchParams.get('unlocked') === 'true';
  const cancelled = searchParams.get('cancelled') === 'true';

  useEffect(() => {
    let cancelledFlag = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (!cancelledFlag) setEventId(r.data.event_id);
      } catch { /* ignore */ }
    })();
    return () => { cancelledFlag = true; };
  }, [slug]);

  const fetchStatus = async () => {
    if (!eventId) return;
    try {
      const r = await axios.get(`${API}/events/${eventId}/year6/memory-book/preview-status`);
      setStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!eventId) return;
    fetchStatus();
    // Poll for ~30s after returning from Stripe in case webhook is slow
    if (unlocked) {
      const t = setInterval(fetchStatus, 2000);
      setTimeout(() => clearInterval(t), 30000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, unlocked]);

  useEffect(() => {
    if (cancelled) toast.error('Checkout cancelled — your data is still safe.');
  }, [cancelled]);

  const handleUnlock = async () => {
    if (!eventId) return;
    setCheckingOut(true);
    try {
      let anonId = null;
      try { anonId = localStorage.getItem('anon_id'); } catch { /* */ }
      const r = await axios.post(`${API}/events/${eventId}/year6/memory-book/checkout`, {
        anon_id: anonId, origin: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error('Could not start checkout');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setCheckingOut(false);
    }
  };

  // STATE 1 — already purchased (or webhook just fired)
  if (status?.purchased) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6" data-testid="year6-mb-page-purchased">
        <div className="max-w-md w-full text-center">
          <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300 mb-5">
            <Check size={28} weight="bold" />
          </div>
          <h1 className="font-serif text-3xl font-black mb-2">Memory Book unlocked! 🎉</h1>
          <p className="text-white/65 mb-7">Your Year 6 Memory Book is being prepared. We'll email it to you when it's ready.</p>
          <a href={`/event/${slug}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }} data-testid="year6-mb-back-event">
            Open class page <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="year6-memory-book-page">
      <div className="max-w-xl mx-auto px-5 pt-10 pb-20">
        {/* Stripe-return banner */}
        {unlocked && !status?.purchased && (
          <div className="mb-5 p-3 rounded-xl bg-amber-300/10 border border-amber-300/25 text-amber-200 text-sm flex items-center gap-2" data-testid="year6-mb-confirming">
            <ArrowClockwise size={16} weight="bold" className="animate-spin" />
            Confirming your payment — this usually takes a few seconds.
          </div>
        )}

        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em] mb-4">
          <GraduationCap size={11} weight="fill" /> Year 6 Memory Book
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black leading-[1.05] mb-2" data-testid="year6-mb-title">
          {(status?.total ?? 0) >= (status?.threshold ?? 10)
            ? <>🎉 Your Year 6<br/>Memory Book is ready</>
            : <>Your Year 6<br/>Memory Book</>}
        </h1>
        <p className="text-white/65 mb-8 text-base">
          {status?.event_name || 'Your class'}{status?.school_name ? ` · ${status.school_name}` : ''}
        </p>

        {/* Blurred preview */}
        <div className="relative rounded-3xl overflow-hidden mb-8 aspect-[3/4] bg-neutral-900" data-testid="year6-mb-preview">
          {status?.preview_photo ? (
            <img
              src={`${BACKEND_URL}${status.preview_photo}`}
              alt="Memory Book preview"
              className="w-full h-full object-cover"
              style={{ filter: 'blur(28px) brightness(0.6)', transform: 'scale(1.1)' }}
            />
          ) : (
            <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #FACC15 100%)', filter: 'blur(8px) brightness(0.8)' }} />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-black/40 backdrop-blur-[2px]">
            <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-4">
              <LockKey size={26} weight="fill" className="text-amber-300" />
            </div>
            <p className="font-serif text-2xl font-black mb-1">Locked</p>
            <p className="text-white/70 text-sm">Unlock to view + download the full book</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2.5 mb-7">
          <Stat icon={<ImgIcon size={14} weight="fill" />} value={status?.photo_count} label="photos" testid="year6-mb-stat-photos" />
          <Stat icon={<ChatCircleText size={14} weight="fill" />} value={status?.message_count} label="messages" testid="year6-mb-stat-messages" />
          <Stat icon={<VideoCamera size={14} weight="fill" />} value={status?.video_count} label="videos" testid="year6-mb-stat-videos" />
        </div>

        {/* Features */}
        <div className="rounded-2xl border border-amber-300/20 p-6 mb-6" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(139,92,246,0.04))' }} data-testid="year6-mb-features">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-5xl font-black">£99</span>
            <span className="text-white/55 text-sm">one-off · whole class</span>
          </div>
          <p className="text-white/60 text-sm mb-5">No subscription. Parents can download the book too.</p>
          <ul className="space-y-2.5 mb-6">
            {[
              'Full class Memory Book PDF',
              'Every photo, message & video included',
              'Personalised certificate for each pupil',
              'Downloadable for parents to keep',
              'High-resolution, print-ready quality',
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                <Check size={16} weight="bold" className="text-amber-300 mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleUnlock}
            disabled={checkingOut}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-black text-white shadow-2xl disabled:opacity-50 hover:scale-[1.01] transition-transform"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
            data-testid="year6-mb-unlock-cta"
          >
            <LockKey size={18} weight="fill" />
            {checkingOut ? 'Opening checkout...' : 'Unlock Memory Book — £99'}
          </button>
          <p className="text-center text-[11px] text-white/45 mt-3 inline-flex items-center justify-center gap-1.5 w-full">
            <Sparkle size={11} weight="fill" className="text-amber-300" /> Secure payment via Stripe · Instant unlock
          </p>
        </div>

        {/* Soft secondary path */}
        <a href={`/event/${slug}`} className="text-center text-[12px] text-white/55 hover:text-white/80 underline underline-offset-2 block" data-testid="year6-mb-keep-collecting">
          Not yet — keep collecting more memories →
        </a>
      </div>
    </div>
  );
}

function Stat({ icon, value, label, testid }) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3 text-center" data-testid={testid}>
      <div className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-amber-300/10 text-amber-200 mb-1.5">{icon}</div>
      <div className="font-mono tabular-nums text-xl font-black">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
    </div>
  );
}
